import { useCallback, useMemo } from 'react'
import type { SlamJson, SlamPoint } from '../../robot-position-setup/types'


function rotatePoint(
    p: SlamPoint,
    cx: number,
    cy: number,
    angleRad: number,
): SlamPoint {
    if (!angleRad) return p
    const cos = Math.cos(angleRad)
    const sin = Math.sin(angleRad)
    const dx = p.x - cx
    const dy = p.y - cy
    return {
        x: cx + dx * cos - dy * sin,
        y: cy + dx * sin + dy * cos,
    }
}

export function useSlamTransform(
    slamJson: SlamJson,
    mapRotation: number,
    scale: number,
    referenceMapSizeM?: { width: number; height: number } | null,
) {
    const normalizedObstacles = useMemo<SlamPoint[]>(() => {
        return (slamJson.obstacles ?? []).map((p) => ({
            x: p.x ?? 0,
            y: p.y ?? 0,
        }))
    }, [slamJson.obstacles])

    const mapRotationRad = useMemo(() => {
        return mapRotation
    }, [mapRotation])

    const rawCenter = useMemo(() => {
        if (normalizedObstacles.length > 0) {
            let minX = Infinity,
                maxX = -Infinity,
                minY = Infinity,
                maxY = -Infinity
            for (const p of normalizedObstacles) {
                if (p.x < minX) minX = p.x
                if (p.x > maxX) maxX = p.x
                if (p.y < minY) minY = p.y
                if (p.y > maxY) maxY = p.y
            }
            return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
        }
        const b = slamJson.bounds || { minX: -10, maxX: 10, minY: -10, maxY: 10 }
        return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 }
    }, [normalizedObstacles, slamJson.bounds])

    const effectiveRotatedBounds = useMemo(() => {
        if (normalizedObstacles.length > 0) {
            let minX = Infinity,
                maxX = -Infinity,
                minY = Infinity,
                maxY = -Infinity
            for (const p of normalizedObstacles) {
                const rp = rotatePoint(p, rawCenter.x, rawCenter.y, mapRotationRad)
                if (rp.x < minX) minX = rp.x
                if (rp.x > maxX) maxX = rp.x
                if (rp.y < minY) minY = rp.y
                if (rp.y > maxY) maxY = rp.y
            }
            return { minX, maxX, minY, maxY }
        }
        return slamJson.bounds || { minX: -10, maxX: 10, minY: -10, maxY: 10 }
    }, [normalizedObstacles, slamJson.bounds, rawCenter, mapRotationRad])

    const getPixelsPerMeter = useCallback(
        (width: number, height: number) => {
            const refSize = referenceMapSizeM
            const hasReferenceSize =
                !!refSize &&
                Number.isFinite(refSize.width) &&
                Number.isFinite(refSize.height) &&
                refSize.width > 0 &&
                refSize.height > 0

            if (hasReferenceSize) {
                const ppmX = (width * 0.88) / refSize.width
                const ppmY = (height * 0.88) / refSize.height
                return Math.min(ppmX, ppmY)
            }

            const { minX, maxX, minY, maxY } = effectiveRotatedBounds
            const spanX = Math.abs(maxX - minX) || 1
            const spanY = Math.abs(maxY - minY) || 1

            const ppmX = (width * 0.88) / spanX
            const ppmY = (height * 0.88) / spanY

            return Math.min(ppmX, ppmY)
        },
        [effectiveRotatedBounds, referenceMapSizeM],
    )

    const slamToScreenPx = useCallback(
        (p: SlamPoint, width: number, height: number) => {
            const ppm = getPixelsPerMeter(width, height)
            const rotated = rotatePoint(p, rawCenter.x, rawCenter.y, mapRotationRad)

            const dx = rotated.x - rawCenter.x
            const dy = rotated.y - rawCenter.y

            return {
                x: width / 2 + dx * ppm,
                y: height / 2 - dy * ppm,
            }
        },
        [rawCenter, mapRotationRad, getPixelsPerMeter],
    )

    const screenPxToSlam = useCallback(
        (baseX: number, baseY: number, width: number, height: number) => {
            const ppm = getPixelsPerMeter(width, height)
            const dx = (baseX - width / 2) / ppm
            const dy = (height / 2 - baseY) / ppm

            const rotatedPt = {
                x: rawCenter.x + dx,
                y: rawCenter.y + dy,
            }

            return rotatePoint(rotatedPt, rawCenter.x, rawCenter.y, -mapRotationRad)
        },
        [rawCenter, mapRotationRad, getPixelsPerMeter],
    )

    const kShrink = useMemo(() => {
        if (scale <= 1) return 0.75
        return Math.min(1, 0.75 + 0.25 * Math.sqrt((scale - 1) / 3))
    }, [scale])

    const pxToCtx = useCallback(
        (screenPx: number) => (screenPx * kShrink) / scale,
        [scale, kShrink],
    )

    return {
        normalizedObstacles,
        mapRotationRad,
        slamToScreenPx,
        screenPxToSlam,
        getPixelsPerMeter,
        kShrink,
        pxToCtx,
    }
}